import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Save, Plus, Trash2, CheckCircle, XCircle, Globe } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSite, setNewSite] = useState({
    site_name: "",
    site_url: "",
    username: "",
    app_password: "",
    cta_url: "https://app.martto.fr",
    company_colors: {
      primary: "#4F86EF",
      secondary: "#3A6B35"
    },
    company_description: "",
    cta_tone: "professionnel",
    default_status: "draft"
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/wordpress/sites`);
      setSites(response.data || []);
    } catch (error) {
      console.error("Erreur chargement sites:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSite = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.post(`${API}/wordpress/config`, newSite);
      toast.success(`Site "${newSite.site_name}" ajouté avec succès !`);
      setShowAddModal(false);
      setNewSite({
        site_name: "",
        site_url: "",
        username: "",
        app_password: "",
        cta_url: "https://app.martto.fr",
        company_colors: {
          primary: "#4F86EF",
          secondary: "#3A6B35"
        },
        company_description: "",
        cta_tone: "professionnel",
        default_status: "draft"
      });
      fetchSites();
    } catch (error) {
      console.error("Erreur ajout site:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout du site");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSite = async (siteId, siteName) => {
    if (!window.confirm(`Supprimer le site "${siteName}" ?`)) return;
    
    try {
      await axios.delete(`${API}/wordpress/config/${siteId}`);
      toast.success("Site supprimé");
      fetchSites();
    } catch (error) {
      console.error("Erreur suppression:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleTestConnection = async (site) => {
    setTesting(site.id);
    try {
      // Test simple en récupérant les catégories
      const response = await axios.post(`${API}/wordpress/config`, {
        site_name: site.site_name,
        site_url: site.site_url,
        username: site.username,
        app_password: site.app_password,
        cta_url: site.cta_url || "https://app.martto.fr",
        default_status: site.default_status
      });
      toast.success("✅ Connexion réussie !");
    } catch (error) {
      toast.error("❌ Connexion échouée");
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Paramètres</h1>
          <p className="text-gray-600">Gérez vos sites WordPress</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Ajouter un site</span>
        </button>
      </div>

      {sites.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <Globe size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-600 mb-4">Aucun site WordPress configuré</p>
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            Ajouter votre premier site
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {sites.map((site) => (
            <div key={site.id} className="bg-white rounded-xl border border-gray-100 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <Globe size={24} className="text-[#4F86EF]" />
                    <h3 className="text-lg font-semibold text-gray-900">{site.site_name}</h3>
                    {site.active && (
                      <span className="badge badge-success text-xs">Actif</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-1">{site.site_url}</p>
                  <p className="text-xs text-gray-500">Utilisateur : {site.username}</p>
                  {site.cta_url && (
                    <p className="text-xs text-[#4F86EF] mt-2">🎯 CTA : {site.cta_url}</p>
                  )}
                  {site.company_colors && (
                    <div className="flex items-center space-x-2 mt-2">
                      <span className="text-xs text-gray-500">Couleurs :</span>
                      <div 
                        className="w-6 h-6 rounded border border-gray-200" 
                        style={{ backgroundColor: site.company_colors.primary }}
                        title={`Primaire: ${site.company_colors.primary}`}
                      ></div>
                      <div 
                        className="w-6 h-6 rounded border border-gray-200" 
                        style={{ backgroundColor: site.company_colors.secondary }}
                        title={`Secondaire: ${site.company_colors.secondary}`}
                      ></div>
                    </div>
                  )}
                  {site.company_description && (
                    <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                      📝 {site.company_description}
                    </p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleTestConnection(site)}
                    disabled={testing === site.id}
                    className="btn-secondary text-sm flex items-center space-x-1"
                  >
                    {testing === site.id ? (
                      <>
                        <div className="loading-spinner-small"></div>
                        <span>Test...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        <span>Tester</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleDeleteSite(site.id, site.site_name)}
                    className="text-red-600 hover:text-red-700 p-2"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Ajout Site */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Ajouter un site WordPress</h2>
            <form onSubmit={handleAddSite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du site *
                </label>
                <input
                  type="text"
                  value={newSite.site_name}
                  onChange={(e) => setNewSite({ ...newSite, site_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
                  placeholder="Ex: Blog Principal, Site Client A"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL du site WordPress *
                </label>
                <input
                  type="url"
                  value={newSite.site_url}
                  onChange={(e) => setNewSite({ ...newSite, site_url: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
                  placeholder="https://votre-site.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom d'utilisateur WordPress *
                </label>
                <input
                  type="text"
                  value={newSite.username}
                  onChange={(e) => setNewSite({ ...newSite, username: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
                  placeholder="admin"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Application Password *
                </label>
                <input
                  type="text"
                  value={newSite.app_password}
                  onChange={(e) => setNewSite({ ...newSite, app_password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
                  placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Créez-le dans WordPress → Utilisateurs → Profil → Mots de passe d'application
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL du CTA / Lead Magnet *
                </label>
                <input
                  type="url"
                  value={newSite.cta_url}
                  onChange={(e) => setNewSite({ ...newSite, cta_url: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
                  placeholder="https://app.martto.fr"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Les CTAs dans les articles renverront vers cette URL
                </p>
              </div>

              {/* Branding - Couleurs */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">🎨 Identité Visuelle</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Couleur Principale *
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={newSite.company_colors.primary}
                        onChange={(e) => setNewSite({ 
                          ...newSite, 
                          company_colors: { ...newSite.company_colors, primary: e.target.value }
                        })}
                        className="w-12 h-10 border border-gray-200 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={newSite.company_colors.primary}
                        onChange={(e) => setNewSite({ 
                          ...newSite, 
                          company_colors: { ...newSite.company_colors, primary: e.target.value }
                        })}
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
                        placeholder="#4F86EF"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Couleur Secondaire *
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={newSite.company_colors.secondary}
                        onChange={(e) => setNewSite({ 
                          ...newSite, 
                          company_colors: { ...newSite.company_colors, secondary: e.target.value }
                        })}
                        className="w-12 h-10 border border-gray-200 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={newSite.company_colors.secondary}
                        onChange={(e) => setNewSite({ 
                          ...newSite, 
                          company_colors: { ...newSite.company_colors, secondary: e.target.value }
                        })}
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
                        placeholder="#3A6B35"
                      />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Ces couleurs seront utilisées pour styliser les CTAs dans les articles
                </p>
              </div>

              {/* Description Entreprise */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description de l'Activité *
                </label>
                <textarea
                  value={newSite.company_description}
                  onChange={(e) => setNewSite({ ...newSite, company_description: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
                  rows={4}
                  placeholder="Ex: Nous sommes une entreprise spécialisée dans les travaux de rénovation énergétique. Nous proposons un simulateur en ligne pour estimer le coût des travaux et mettre en relation avec des artisans qualifiés RGE."
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  L'IA utilisera cette description pour créer des CTAs cohérents avec votre activité
                </p>
              </div>

              {/* Ton des CTAs */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ton des CTAs
                </label>
                <select
                  value={newSite.cta_tone}
                  onChange={(e) => setNewSite({ ...newSite, cta_tone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F86EF] focus:border-transparent"
                >
                  <option value="professionnel">Professionnel</option>
                  <option value="commercial">Commercial</option>
                  <option value="accessible">Accessible et convivial</option>
                  <option value="technique">Technique et expert</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Le style de rédaction des appels à l'action
                </p>
              </div>

              <div className="flex items-center space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 btn-secondary"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  {saving ? "Ajout..." : "Ajouter le site"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .loading-spinner-small {
          border: 2px solid #f3f4f6;
          border-top: 2px solid #4F86EF;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
